/** Client-side API layer: typed fetch wrappers for the dashboard endpoints. */

export interface Suggestion {
  label: string;
  address: string | null;
  lat: number | null;
  lon: number | null;
  postalCode: string | null;
}

/** Properties carried by each decision feature (drives popups + marker color). */
export interface DecisionProps {
  id: string;
  dr_id: string;
  version: number;
  address: string | null;
  decision_date: string | null;
  decision_type: string | null;
  appl_type: string | null;
  decision_type_norm: string | null;
  submission_no: string | null;
  decision_no: string | null;
  submission_desc: string | null;
  mkts_lotno: string | null;
  postal_district: string | null;
  postal_code: string | null;
  has_history: boolean;
}

export interface DecisionFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: DecisionProps;
}

/** A decision matching the filters that has no map location (same fields, no geometry). */
export type UnmappableDecision = DecisionProps;

export interface DecisionFeatureCollection {
  type: "FeatureCollection";
  features: DecisionFeature[];
  unmappable_count: number;
  unmappable: UnmappableDecision[];
  truncated: boolean;
}

export interface DecisionQuery {
  bbox?: [number, number, number, number]; // minLng, minLat, maxLng, maxLat
  from?: string;
  to?: string;
  types?: string[];
  outcomes?: string[];
  district?: string;
  street?: string;
  keyword?: string;
}

/** Build the /api/decisions query string from a query object. */
export function decisionsUrl(q: DecisionQuery): string {
  const params = new URLSearchParams();
  if (q.bbox) params.set("bbox", q.bbox.join(","));
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  if (q.types && q.types.length > 0) params.set("types", q.types.join(","));
  if (q.outcomes && q.outcomes.length > 0) params.set("outcomes", q.outcomes.join(","));
  if (q.district) params.set("district", q.district);
  if (q.street) params.set("street", q.street);
  if (q.keyword) params.set("keyword", q.keyword);
  return `/api/decisions?${params.toString()}`;
}

export async function fetchDecisions(q: DecisionQuery): Promise<DecisionFeatureCollection> {
  const res = await fetch(decisionsUrl(q));
  if (!res.ok) throw new Error(`decisions request failed: HTTP ${res.status}`);
  return (await res.json()) as DecisionFeatureCollection;
}

export async function fetchAppTypes(): Promise<string[]> {
  const res = await fetch("/api/apptypes");
  if (!res.ok) throw new Error(`apptypes request failed: HTTP ${res.status}`);
  const body = (await res.json()) as { types: string[] };
  return body.types;
}

export async function fetchSuggest(q: string): Promise<Suggestion[]> {
  const res = await fetch(`/api/suggest?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(`suggest request failed: HTTP ${res.status}`);
  const body = (await res.json()) as { suggestions: Suggestion[] };
  return body.suggestions;
}
